import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User as UserIcon, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import { useToast } from '../hooks/use-toast';
import FloatingBadge from '../components/three/FloatingBadge';
import BrandLogo from '../components/BrandLogo';

export default function Signup() {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast({ title: 'Please fill all fields', variant: 'destructive' }); return; }
    if (form.password.length < 6) { toast({ title: 'Password must be at least 6 characters', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast({ title: 'Welcome to Skillbridge.guru!', description: 'Your account is ready.' });
      navigate('/');
    } catch (err) {
      toast({ title: 'Signup failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-16 min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BrandLogo size="lg" showWordmark={true} />
          </Link>
          <h1 className="text-3xl font-extrabold mb-2">Create your account</h1>
          <p className="text-gray-500 mb-8">Join over 1 million learners advancing their careers.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Full name</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="Jane Doe" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={show ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="Min. 6 characters" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <p className="text-xs text-gray-500">By signing up you agree to our <a className="text-[#6b47dc] hover:underline" href="#">Terms</a> and <a className="text-[#6b47dc] hover:underline" href="#">Privacy Policy</a>.</p>
            <Button type="submit" disabled={loading} className="w-full bg-[#ff5722] hover:bg-[#e64a19] text-white font-bold h-12 btn-shine">
              {loading ? 'Creating account...' : <>Create account <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-8">Already have an account? <Link to="/login" className="text-[#6b47dc] font-bold hover:underline">Sign in</Link></p>
        </div>
      </div>

      <div className="relative overflow-hidden bg-gradient-to-br from-[#ff5722] via-[#ff7043] to-[#6b47dc] hidden lg:flex items-center justify-center order-1 lg:order-2 p-12">
        <div className="relative z-10 text-white text-center max-w-md">
          <FloatingBadge color="#ffffff" height="260px" />
          <h2 className="text-4xl font-extrabold mt-4 mb-4">Start learning today</h2>
          <p className="opacity-90 mb-6">Get 7-day free access to any course. Cancel anytime.</p>
          <div className="space-y-2 text-left">
            {['Live instructor-led classes', '400+ industry projects', 'Lifetime LMS access', 'Career support'].map(x => (
              <div key={x} className="flex items-center gap-2"><CheckCircle2 size={18} /> <span className="text-sm">{x}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
